import {
    Component,
    ContentChildren,
    EventEmitter,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    Output,
    QueryList,
    SimpleChanges,
    ViewChild
} from '@angular/core';
import {ConfirmationService} from 'primeng/api';
import {FieldStorageService} from "../../../form/field/service/field-storage.service";
// TM-1000001714: Trashbin v2 — typed delete response + deletion type enum
import {DeleteResponseDto} from "../../../dto/delete-response.dto";
import {EntityStatusEnum} from "../../../entity/enum/entity-status.enum";
import {ServerFieldError} from "../mvs-crud-object-form/server-field-error.interface";
import {FormSmartGuideFocusEvent} from "../../../form/dto/form-smart-guide-focus-event.dto";
import {FormFooterButton} from "../mvs-crud-object-form/dto/dorm-custom-footer-button.interface";
import {FormActionEnum} from "../../../service/crud/enum/form-action.enum";
import {MvsCrudObjectInterfaceComponent} from "../../../logic/form/logic/mvs-crud-object-interface.component";
import {ObjectChangeInformation, ObjectChangeInformationActionEnum} from "../../../logic/object-change-information";
import {WidgetHelperButtonEvent} from "../../../basic/widget-helper-button-event";
import {WidgetHelperButton} from "../../../basic/widget-helper-button";
import {MvsFormEventCrudAction} from "../../../logic/form/logic/mvs-form-event-crud-action";
import {DtoDetail} from "../../../dto/dto.detail";
import {MvsCoreService} from "../../../service/mvs-core.service";
import {MvsMessageService} from "../../../service/message/mvs-message.service";
import {MvsTemplateDirective} from "../../../templating/directive/mvs-template.directive";
import {MvsFormFieldsOverwriteInterface} from "../../../logic/form/logic/mvs-form-fields-overwrite.interface";
import {FormModeEnum} from "../../../dto/object/data/form-mode.enum";
import {MvsFormDto} from "../../../dto/form/dto/mvs-form.dto";
import {MvsCrudService} from "../../../service/crud/mvs-crud.service";
import {MvsCrudObjectHistoryService} from "../../service/mvs-crud-object-history.service";
import {DeletionType} from "../../../entity/enum/deletion-type.enum";
import {MvsCrudObjectFormComponent} from "../mvs-crud-object-form/mvs-crud-object-form.component";
import {MvsFormStateDto} from "../mvs-crud-object-form/dto/mvs-form-state.dto";
@Component({
    selector: 'mvs-crud-object-generic',
    templateUrl: './mvs-crud-object-generic.component.html',
    standalone: false
})
export class MvsCrudObjectGenericComponent extends MvsCrudObjectInterfaceComponent implements OnInit, OnDestroy, OnChanges {

    constructor(
        protected coreService: MvsCoreService,
        protected messageService: MvsMessageService,
        protected fieldStorageService: FieldStorageService,
        protected confirmationService: ConfirmationService,
        protected historyService: MvsCrudObjectHistoryService) {
        super();
    }

    // TM-1000001714: When set, the embedded form renders an inline warning p-message
    // (with a "Rückgängig" button) and hides every footer button. Set on a successful
    // soft-delete; cleared on undo or when a new entity is loaded into the form.
    // TM-1000001713: The banner is rendered in the form's STICKY HEADER region (top of the
    // form, directly under the mode/use-case header), not in the footer. The footer simply
    // collapses to nothing while softDeletedNotice is set.
    softDeletedNotice: { id: number; message: string } | null = null;

    @Input() formFooterCustomButtons: FormFooterButton[] = [];
    @ContentChildren(MvsTemplateDirective) templates: QueryList<MvsTemplateDirective>;
    @Input() override objectType: string;
    @Input() fieldOverwrite: MvsFormFieldsOverwriteInterface;
    @Input() removeDelete: boolean = false;
    @Input() askForConfirmation: boolean = false; // ask for confirmation before saving the form
    @Input() sgUseCaseId: number;
    @Input() formMode: FormModeEnum;
    @Input() forceHideModes: boolean;
    @Input() autoSaveMode: boolean = false;

    /**
     * The still-stand before {@link #autoSaveMode} writes — forwarded to the embedded form, see
     * MvsCrudObjectFormComponent#autoSaveDebounceMs.
     *
     * Additive and unchanged by default: 1000 is the value the form used before this input existed.
     */
    @Input() autoSaveDebounceMs: number = 1000;

    /**
     * @ticket TM-1000002003
     * Let the embedded form answer a saved row and a changed {@link #formControlOverwrite} by
     * UPDATING its live form group instead of rebuilding it — see
     * MvsCrudObjectFormComponent#inPlaceUpdate. With {@link #autoSaveMode} on, the rebuild destroys
     * the input the user is typing in. Additive and OFF by default.
     */
    @Input() inPlaceUpdate: boolean = false;

    @Output() beforeSaveCallback = new EventEmitter<Function>();

    @Output() onFormTargetFocus = new EventEmitter<FormSmartGuideFocusEvent>;

    /**
     * The embedded generated form. Only used to forward {@link #save} — the caller-owned save gate.
     * Not static: the form sits behind *ngIf="initialized".
     */
    @ViewChild(MvsCrudObjectFormComponent) protected formComponent: MvsCrudObjectFormComponent;



    dto: DtoDetail;
    form: MvsFormDto;

    apiErrors: ServerFieldError[];
    crudService: MvsCrudService;

    isCustomButton: boolean;
    widgetHelperButtonEvent: WidgetHelperButton;

    formFocusEvent: FormSmartGuideFocusEvent;

    createMode: boolean = false;
    initialized: boolean = false;
    busy: boolean = false;

    /**
     * Initialize component. We expect all relevant information to be already provided.
     */
    ngOnInit(): void {

        // refresh component (based on provided data)
        this.refreshComponent(true);

    }

    /**
     * Execute finale initialization steps after data was retrieved.
     */
    initFinalize(): void {
        this.initialized = true;
    }

    /**
     * Refresh Data, either load object information or template details.
     */
    refreshData(init: boolean): void {

        if (!this.createMode) {
            // we'll retrieve the object ID information

            // retrieve DTO
            this.crudService.get(this.objectId, this.importObjectContextDto, true, true, 0, FormActionEnum.on_init_change, this.formMode, this.sgUseCaseId).subscribe(dtoDetail => {

                // set DTO
                this.dto = dtoDetail;
                this.form = dtoDetail.form;

                // TM-1000001714: if the loaded entity is already soft-deleted (entityStatus = deleted),
                // open the form in the disabled+undo state. Safe for entities without entityStatus.
                this.evaluateSoftDeletedNoticeFromDto(dtoDetail);

                // set form initialized (if not happened yet)
                if (init) {
                    this.initFinalize();
                }
            });


        } else {

            // take over default DTO information
            if (this.defaultCreateDto && !this.dto) {
                this.dto = this.defaultCreateDto;
            }

            this.initFinalize();

        }
    }

    handleTemplateRefresh(form: MvsFormDto) {
        this.onTemplateRefresh.emit(form);
    }

    /**
     * Refresh Component.
     */
    refreshComponent(init: boolean): void {

        // decied whether we run in create or update mode
        if (this.objectId) {
            this.createMode = false;
        } else {
            // could also be that we are taking over the dto from somewhere else
            if (this.defaultCreateDto && this.defaultCreateDto.id) {
                this.objectId = this.defaultCreateDto.id;
                this.createMode = false
            } else {
                this.createMode = true;
            }
        }

        // retrieve specific CRUD service used for the Entity
        if (!this.crudService || this.crudService.objectType !== this.objectType) {
            this.crudService = this.coreService.getCrudService(this.objectType);
        }

        // refresh data
        this.refreshData(init);

    }

    /**
     * Handle Lifecycle events.
     * @param event
     */
    override onFormLifeCycleCrudAction(event: MvsFormEventCrudAction) {

        // CRUD actions need to be handled within sub methods
        switch (event.crudAction) {
            case "update":
                this.handleOnUpdate(event.dto);
                break;
            case "create":
                this.handleOnCreate(event.dto);
                break;
            case "delete":
                this.handleOnDelete(event.dto);
                break;
        }

    }

    /**
     * Handle on create.
     * @param dto
     */
    handleOnCreate(dto: DtoDetail) {


        this.busy = true;

        this.crudService.create(dto, this.importObjectContextDto)
            .subscribe((dto) => {

                    // show success message
                    this.messageService.showSuccessMessage("Angelegt", null);

                    // change from create mode to edit mode
                    this.createMode = false;
                    this.clearLocalStorage();
                    this.objectId = dto["id"];
                    this.dto = dto;
                    this.form = dto.form;

                    // TM-1000001714: defensive — a freshly created entity should never be in deleted state
                    this.evaluateSoftDeletedNoticeFromDto(dto);

                    this.fireEventChanged(ObjectChangeInformation.create(this.crudService.objectType, ObjectChangeInformationActionEnum.created, null, this.dto));

                    this.apiErrors = [];
                    this.busy = false;


                }, error => {
                    // this.messageService.showErrorMessage("Fehler aufgetreten", error);
                    this.handleOnError(error);
                    this.busy = false;
                    this.onObjectChangeError.emit(error);
                }
            );
    }

    handleOnError(errorResponse: any) {


        this.apiErrors = [];

        if (errorResponse.error && errorResponse.error.length > 0) {
            this.apiErrors = errorResponse.error;

            // Extract all default messages and join them with newline characters
            const mergedMessages = this.apiErrors.map(error => error.defaultMessage).join('\n');

            this.messageService.showErrorMessage("Speichern nicht möglich", mergedMessages);

        }

    }

    handleOnUpdate(dto: DtoDetail) {
        if (this.askForConfirmation) {

            this.beforeSaveCallback.emit(() => {
                this.handleUpdate(dto)
            });

        } else {
            this.handleUpdate(dto)
        }
    }

    handleUpdate(dto: DtoDetail) {
        this.busy = true;

        this.crudService.update(dto, this.importObjectContextDto)
            .subscribe((dto) => {
                    this.messageService.showSuccessMessage("Gespeichert", null);
                    this.clearLocalStorage();
                    this.dto = dto;

                    // TM-1000001714: keep the soft-deleted notice in sync with the post-save state
                    this.evaluateSoftDeletedNoticeFromDto(dto);

                    this.fireEventChanged(ObjectChangeInformation.create(this.crudService.objectType, ObjectChangeInformationActionEnum.updated, this.dto, dto));
                    this.apiErrors = [];
                    this.busy = false;

                    // TM-1000001467: Refresh history panel after save
                    this.historyService.refreshHistoryPanel();

                }, error => {
                    // this.messageService.showErrorMessage("Fehler aufgetreten", error);
                    this.handleOnError(error);
                    this.busy = false;
                    this.onObjectChangeError.emit(error);
                }
            );
    }

    // TM-1000001714: Server auto-routes by EntityStatus (soft vs. hard) — UI keeps the
    // pre-ticket confirmation wording; differentiation surfaces only in the response handling.
    handleOnDelete(dto: DtoDetail) {

        this.busy = true;

        // make sure that the user actually wants to delete
        this.confirmationService.confirm({
            // target: event.target,
            message: 'Bist du dir sicher, dass du den Eintrag löschen möchtest?',
            icon: 'pi pi-exclamation-triangle',
            accept: () => {

                this.crudService.delete(dto["id"]).subscribe((response: DeleteResponseDto) => {

                    this.handleDeleteResponse(dto["id"], response);

                }, error => {
                    this.messageService.showSuccessMessage("Fehler aufgetreten", error);
                    this.busy = false;
                });
            },
            reject: () => {
                //reject action
                this.busy = false;
            }
        });

    }

    /**
     * Process a {@link DeleteResponseDto} and drive the appropriate UX feedback.
     *
     * SOFT + restorable -> dialog stays open with an inline p-message (Rückgängig) at the TOP
     *                      of the embedded form, in the sticky header region (driven by
     *                      softDeletedNotice). The pre-ticket "Gelöscht" success toast is
     *                      reused unchanged.
     * HARD               -> change event, form returns to create mode, same "Gelöscht" toast.
     *
     * @ticket TM-1000001713
     */
    // TM-1000001714: Drives the inline footer notice based on the typed response; toast + change
    // event remain identical to the pre-ticket behaviour.
    handleDeleteResponse(deletedId: number, response: DeleteResponseDto) {

        this.fireEventChanged(ObjectChangeInformation.create(this.crudService.objectType, ObjectChangeInformationActionEnum.deleted, this.dto, null));

        // determine soft vs. hard
        const isSoftRestorable = response?.deletionType === DeletionType.SOFT && response?.restorable === true;

        if (isSoftRestorable) {
            // TM-1000001714: Soft delete — DO NOT auto-close, DO NOT reset to createMode.
            // The dialog stays open so the inline p-message (with Rückgängig) is visible.
            // No toast for soft delete — the inline banner is the only feedback.
            this.softDeletedNotice = {
                id: deletedId,
                message: response.message ?? 'Eintrag wurde in den Papierkorb verschoben.'
            };
        } else {
            // HARD delete — pre-ticket behaviour: reset form to create mode + show toast.
            this.dto["id"] = null;
            this.createMode = true;
            this.softDeletedNotice = null;
            this.messageService.showSuccessMessage("Gelöscht", null);
        }

        this.busy = false;
    }

    /**
     * TM-1000001714: Detect whether the loaded dto represents an already soft-deleted entity
     * (entityStatus = deleted) and, if so, set softDeletedNotice so the form opens in the
     * disabled+undo state. Otherwise clears any previous notice.
     *
     * Defensive against entities that don't implement EntityStatus (no entityStatus field):
     * if the field is absent / null / undefined, nothing happens.
     *
     * Defensive against wire-format ambiguity: backend may serialize the enum by NAME ("deleted")
     * or by ORDINAL (5, the implicit value of EntityStatusEnum.deleted). Both are accepted.
     */
    private evaluateSoftDeletedNoticeFromDto(dto: DtoDetail): void {
        if (!dto) {
            this.softDeletedNotice = null;
            return;
        }
        const status = (dto as any)["entityStatus"];
        if (status === undefined || status === null) {
            // entity does not implement EntityStatus — nothing to do
            this.softDeletedNotice = null;
            return;
        }
        const isDeleted = status === EntityStatusEnum.deleted
            || status === 'deleted'
            || status === EntityStatusEnum[EntityStatusEnum.deleted]; // converts ordinal->name as a final defensive check
        if (isDeleted) {
            this.softDeletedNotice = {
                id: dto["id"],
                message: 'Eintrag wurde in den Papierkorb verschoben.'
            };
        } else {
            this.softDeletedNotice = null;
        }
    }

    /**
     * TM-1000001714: Click handler for the inline "Rückgängig" button emitted by mvs-crud-object-form.
     * Reuses the existing {@link handleRestoreClick} logic (restore call, change event, toast),
     * then clears {@link softDeletedNotice} so the sticky warning banner disappears, the form
     * footer's standard delete/save buttons reappear, and the form returns to its normal edit mode.
     *
     * Assumption: restore only flips an EntityStatus field server-side. The in-memory dto is still
     * valid post-restore, so no re-fetch is performed — the change event triggers parent widgets
     * to refresh themselves on the channel they already listen on.
     *
     * @ticket TM-1000001713
     */
    onUndoSoftDeleteFromForm(deletedId: number) {
        // reuse the existing restore logic — emits change event + success/error toast
        this.handleRestoreClick(deletedId);
        // form returns to standard edit mode; footer reverts to the delete button
        this.softDeletedNotice = null;
    }

    /**
     * TM-1000001714: Undo handler — restore the soft-deleted entity and broadcast an update event
     * so parent widgets refresh on the same channel as every other CRUD event in this component.
     */
    handleRestoreClick(deletedId: number) {

        if (deletedId == null) {
            return;
        }

        this.crudService.restore(deletedId).subscribe(() => {

            // TM-1000001714: keep the local in-memory dto's entityStatus in sync with the server-side flip
            if (this.dto && this.dto["entityStatus"] !== undefined) {
                this.dto["entityStatus"] = EntityStatusEnum.active;
            }

            // refresh signal — the same channel as every other CRUD event in this component
            const restoredDto = new DtoDetail();
            restoredDto["id"] = deletedId;
            this.fireEventChanged(ObjectChangeInformation.create(this.crudService.objectType, ObjectChangeInformationActionEnum.updated, null, restoredDto));

            this.messageService.showSuccessMessage("Wiederhergestellt", null);

        }, error => {
            this.messageService.showErrorMessage("Fehler aufgetreten", error);
        });
    }

    onBack() {
        this.onBackEvent.emit();
    }


    fireEventChanged(changeInfo: ObjectChangeInformation) {
        this.onChangedObject.emit(changeInfo);
        if (this.isCustomButton) {
            const event: WidgetHelperButtonEvent = new WidgetHelperButtonEvent()
            event.widgetHelperButton = this.widgetHelperButtonEvent;
            event.changeInfo = changeInfo;
            this.onFooterButtonClick.emit(event);
            this.widgetHelperButtonEvent = null;
            this.isCustomButton = false;
        }
    }

    clearLocalStorage() {
        let objectId: number;
        if (this.objectId != undefined) {
            objectId = this.objectId;
        } else {
            objectId = 0;
        }
        this.fieldStorageService.clearFieldDataRange(this.objectType, objectId);
    }

    handleButtonClick(object: { event: WidgetHelperButton; action: MvsFormEventCrudAction }) {
        this.isCustomButton = true;
        this.widgetHelperButtonEvent = object.event;
        if (object.action instanceof MvsFormEventCrudAction) {
            this.onFormLifeCycleCrudAction(object.action);
        }
    }

    handleFormDirty(formDirtyState: boolean) {
        this.onFormDirty.emit(formDirtyState);
    }

    handleFormChange(dto: DtoDetail) {
        this.onFormChange.emit(dto);
    }

    handleFormSubmitStateChange(submittable: boolean) {
        this.onFormSubmitStateChange.emit(submittable);
    }

    handleFormStateChange(state: MvsFormStateDto) {
        this.onFormStateChange.emit(state);
    }

    /**
     * Caller-owned save — delegates to the embedded form, which then routes back into this
     * component's own handleOnCreate / handleOnUpdate. Identical to a click on the built-in button.
     */
    override save(action?: 'create' | 'save'): boolean {
        return this.formComponent ? this.formComponent.triggerAction(action) : false;
    }

    handleFormTargetFocus(event: FormSmartGuideFocusEvent) {

        if (this.formFocusEvent) {
            const isSameType = event.type === this.formFocusEvent.type;
            const isSameField = event.type === 'FIELD' && this.formFocusEvent.fieldName === event.fieldName;
            const isSameGroup = event.type === 'GROUP' && this.formFocusEvent.groupName === event.groupName;

            if (isSameType && (isSameField || isSameGroup)) {
                return; // no need to return the same focused field again as the caller already have this information
            }
        }

        this.formFocusEvent = event;
        this.onFormTargetFocus.emit(event);
    }


    /**
     * Process Changes.
     * @param changes
     */
    ngOnChanges(changes: SimpleChanges) {

        if (!this.initialized) {
            return;
        }

        // check whether objectId changed and load object details
        if (changes["objectId"]) {

        }

        // An access overwrite is a RENDER decision about the row already on screen, never a different row.
        // Re-fetching for it throws the user's unsaved input away and costs a round trip for nothing. When
        // it is the only input that changed, let the binding hand it to the embedded form, which rebuilds
        // its controls over the values it already holds (MvsCrudObjectFormComponent.ngOnChanges).
        if (changes["formControlOverwrite"] && Object.keys(changes).length === 1) {
            return;
        }

        this.refreshComponent(false);
    }

    /**
     * Clean up.
     */
    ngOnDestroy(): void {
    }

}
